export default customElements.define('w-field-address-search', class FieldSearch extends wealthify.CustomElementBaseClass {

    #styleSelector;

    constructor() {
        const styleSelector = 'c-field-address-search';

        const config = {
            styleSelector: styleSelector,
            templateHTML: `
            <div class="${styleSelector}__wrapper" data-root>
                 <w-field-textbox class="u-margin__bottom-mobile--0" validate-required="true" label="Search for an address" data-address-search placeholder="Start typing a postcode, street or address..."></w-field-textbox>
                    
                    <div class="${styleSelector}__search-results u-margin__bottom-mobile--spacing-xl">
                                <div class="u-display__none" data-search-area>
                                    <ul class="${styleSelector}__list" role="listbox" data-search-results>
                                    </ul>
                                    <div class="${styleSelector}__total" data-search-footer role="status" aria-live="polite" aria-atomic="true"></div>
                                </div>
                    </div>
            </div>`,
            hasChildren: true,
            cacheDom: [ 'data-address-search', 'data-search-area', 'data-search-results', 'data-search-footer']
        };

        super(config);

        this.onConnectedCallback = () => {
            this.#setup();
            this.#styleSelector = styleSelector;
        };
    }

    static eventSettings = Object.freeze({events: {ValueChange: 'valuechange'}});

    static requiredConfigProperties = [
        'key', 'url', 'retrieveUrl', 'limit'
    ]

    static get observedProps () {
        return [
            new wealthify.Prop('config', {
                type: wealthify.propTypes.Object(),
                validators: [wealthify.propValidators.ObjectContainsKeys(this.requiredConfigProperties)]
            })
        ];
    }

    get propCallbacks () {
        return {config: (value) => {
            if(!value.key){  
                throw new Error('Require config key');
            }
        } };
    }

    #setup() {
        const resultArea = this.dom.searchResults;

        document.addEventListener('pointerup', (e) => {
            if (resultArea.contains(e.target)) {
                this.#toggleList(true);
            } else {
                this.#toggleList(false);
            }
        });

        this.dom.addressSearch.addEventListener('keyup', (e) => {
            this.#userKeyPress(e);
        });

        this.dom.addressSearch.addEventListener('click', () => {
            const isNotOpened = !this.isOpen;
            const weHaveSearchItems = this.searchItems?.length > 0 || false;
            const showList = isNotOpened && weHaveSearchItems;

            this.#toggleList(showList);
        });

        resultArea.addEventListener('click', (e) => {
            const option = e.target;
            const selectedItem = option.id;

            const selectedOption = this.searchItems.find(x => x.Id === selectedItem);

            this.#setOption(selectedOption);
            this.#searchItem(this.selected);
        });

        this.dom.addressSearch.addEventListener(this.dom.addressSearch.events.ValueChange.name, () => {
            const canSearch = this.dom.addressSearch.value.length > 3;

            if (!canSearch || this.addressFound) {
                return;
            }
            this.#searchAddress(this.dom.addressSearch.value);
        });

        this.#setAttributes();
    }

    #toggleList(open = true) {
        this.#showElement(this.dom.searchArea, open);
        this.#expandSearch(open);

        if (!open) {
            if (this.searchFooterTimer) {
                clearTimeout(this.searchFooterTimer);
            }

            this.selected = null;
        }
    }

    #userKeyPress(event) {
        if (event.ctrlKey || event.shiftKey) {
            return;
        }

        switch (event.key) {
        case 'Enter':
            if (this.selected) {
                this.#searchItem(this.selected);
            }
            break;

        case 'Down':
        case 'ArrowDown':
            if (this.searchItems.length > 0) {
                const nextLocation = this.#getNextItem(this.selected);

                this.#setOption(nextLocation);
            }
            break;

        case 'Up':
        case 'ArrowUp':
            if (this.searchItems.length > 0) {
                const nextLocation = this.#getPreviousItem(this.selected);

                this.#setOption(nextLocation);
            }
            break;

        case 'Backspace':
            this.#toggleList(false);
            this.#resetSearch();
            this.selected = null;
            break;
        case 'Esc':
        case 'Escape':
            this.#toggleList(false);
            this.#resetSearch();
            this.dom.addressSearch.value = '';
            break;
        }
    }

    #setOption(selected) {
        this.selected = selected;
        this.#setCurrentOptionStyle(this.selected);
    }

    #searchItem(selected) {
        const addressId = selected.Id;
        const type = selected.Type;

        if (type !== 'Address') {
            this.#searchAddress(addressId, true);

        } else {
            this.#retrieveAddress(addressId);
            this.dom.addressSearch.value = this.#addressFormatter(selected);
        }
    }

    #searchAddress(text, widerSearch = false) {
        const limit = widerSearch ? 300 : this.config.limit;

        this.selected = null;

        const config = {
            contentType: 'application/json',
            method: 'GET',
            url: `${this.config.url}?Key=${this.config.key}
                &Limit=${limit}
                &Countries=GB,JE,GGY`
        };

        if (widerSearch) {
            config.url += `&Container=${text}`;
        } else {
            config.url += `&Text=${text}`;
        }

        wealthify.request.send(config).then(response => {
            const locations = Array.from(response.Items);

            this.#renderSearchItems(locations, 'Id');

            if (widerSearch) {
                if (this.searchItems.length > 0) {
                    const nextLocation = this.#getNextItem(this.selected);

                    this.#setOption(nextLocation);
                }
            }
        });

        this.#foundAddress(false);
    }

    #setAttributes() {
        const areaControlId = 'unique_search';

        this.dom.addressSearch.ariaRoleProp = 'combobox';
        this.dom.addressSearch.ariaControlsProp = areaControlId;

        // Make aria-controls id dynamic match the input with the result area
        this.dom.searchResults.id = areaControlId;
    }


    #retrieveAddress(addressId) {
        const config = {
            contentType: 'application/json',
            method: 'GET',
            url: `${this.config.retrieveUrl}?Key=${this.config.key}
            &Id=${addressId}`
        };

        wealthify.request.send(config).then(response => {
            let addressDetail = response.Items[0];

            const {
                Line1: line1, Line2: line2, Line3: line3, City: city, PostalCode: postcode, CountryIso3: isoCode
            } = addressDetail;

            this.#dispatchEvent({
                line1: line1,
                line2: line2,
                line3: line3,
                city: city,
                postcode: postcode,
                isoCode: isoCode
            });
        });

        this.#toggleList(false);
        this.#foundAddress(true);
    }

    #renderSearchItems(items, keyId) {
        const resultArea = this.dom.searchResults;

        let children = [];

        for (let item of items) {
            const text = this.#addressFormatter(item);
            const listItem = document.createElement('li');

            listItem.classList.add(`${this.#styleSelector}__list-item`);
            listItem.setAttribute('role', 'option');
            listItem.innerHTML = text;
            listItem.id = item[keyId];

            children.push(listItem);
        }

        this.searchItems = items;

        if (this.searchItems && Array.isArray(this.searchItems) && this.searchItems.length > 0) {
            this.#toggleList();
            this.#addCustomFooter(items.length);
        }

        resultArea.replaceChildren(...children);
    }


    #showElement(element, show = true) {
        const addOrRemove = show ? 'remove' : 'add';

        element.classList[addOrRemove]('u-display__none');
    }

    get isOpen() {
        const isOpen = !this.dom.searchArea.classList.contains('u-display__none');

        return isOpen;
    }

    #expandSearch(value) {
        this.dom.addressSearch.ariaExpandedProp = value;
    }

    #addCustomFooter(length, remove = false) {
        const footer = this.dom.searchFooter;

        if (footer.children.length > 0) {
            while (footer.firstChild) {
                footer.removeChild(footer.firstChild);
            }
        }

        if (this.searchFooterTimer) {
            clearTimeout(this.searchFooterTimer);
        }

        if (!remove) {
            const element = document.createElement('p');

            this.#addressFooter(element, length);

            this.searchFooterTimer = setTimeout(() => {
                footer.appendChild(element);
            }, 250);
        }
    }

    #dispatchEvent(address) {
        const event = new CustomEvent(this.events.ValueChange.name, {detail: address});

        this.dispatchEvent(event);
    }

    #addressFooter(element, length) {
        if (length === 0) {
            element.textContent = 'no matching address found';

        } else {
            element.textContent = `${length} ${length === 1 ? 'address' : 'addresses'} available`;
        }
    }

    #addressFormatter(location, clean = false) {
        if (location.Description.includes('Addresses')) {
            const lastDash = location.Description.lastIndexOf('-');
            const addressCount = location.Description.substring(lastDash);

            return clean ? `${location.Text}` : `${location.Text} <i class='${this.#styleSelector}__list-count'> ${addressCount} </i>`;
        }

        return `${location.Text} ${location.Description}`;
    }


    #setCurrentOptionStyle(selected) {
        const resultArea = this.dom.searchResults;

        const previouslySelected = resultArea.querySelector('[aria-selected="true"]');

        if (previouslySelected) {
            previouslySelected.removeAttribute('aria-selected');
            previouslySelected.classList.remove(`${this.styleSelector}__list-item--selected`);
        }

        const selectedOption = resultArea.querySelector(`[id="${selected.Id}"]`);

        if (selectedOption) {
            selectedOption.setAttribute('aria-selected', 'true');
            selectedOption.classList.add(`${this.styleSelector}__list-item--selected`);
            
            selectedOption.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });

            this.#setActivedescendant(selected.Id);
        }
    }

    #getPreviousItem(currentSelectedItem) {
        if (!currentSelectedItem) {
            return this.searchItems[this.searchItems.length - 1];
        }

        const currentAddress = this.searchItems.find(x => x.Id === currentSelectedItem.Id);
        const firstAddress = this.searchItems[0];

        if (currentSelectedItem.Id !== firstAddress.Id) {
            const index = this.searchItems.indexOf(currentAddress);

            return this.searchItems[index - 1];
        }

        return this.searchItems[this.searchItems.length - 1];
    }

    #getNextItem(currentSelectedItem) {
        if (!currentSelectedItem) {
            return this.searchItems[0];
        }

        const currentAddress = this.searchItems.find(x => x.Id === currentSelectedItem.Id);
        const lastAdress = this.searchItems[this.searchItems.length - 1];

        if (currentSelectedItem.Id !== lastAdress.Id) {
            const index = this.searchItems.indexOf(currentAddress);

            return this.searchItems[index + 1];
        }

        return this.searchItems[0];
    }

    #setActivedescendant(id) {
        this.dom.addressSearch.ariaActiveDescendantProp = id ?? '';
    }

    #resetSearch() {
        this.selected = null;
        this.searchItems = null;
        this.addressFound = false;

        this.#dispatchEvent({
            line1: '',
            line2: '',
            line3: '',
            city: '',
            postcode: '',
            isoCode: ''
        });
    }

    #foundAddress(value = false) {
        if (value) {
            this.searchItems = null;
            this.selected = null;
        }
        this.addressFound = value;
    }
});